import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import DOMPurify from "dompurify";
import { Clock, ChevronRight, ArrowLeft, ShieldAlert } from "lucide-react";
import { api, SITE_URL } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { AdSlot } from "@/components/AdSlot";
import { ArticleCard } from "@/components/ArticleCard";

export default function ArticleDetail() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    setStatus("loading");
    api
      .get(`/articles/${slug}`)
      .then((r) => {
        setArticle(r.data);
        setStatus("ok");
      })
      .catch(() => setStatus("error"));
  }, [slug]);

  useSeo({
    title: article ? `${article.title} | Licence to Play` : "Loading… | Licence to Play",
    description: article?.meta_description,
    keywords: article?.keywords?.join(", "),
    image: article?.image_url,
    path: `/dossier/${slug}`,
    type: "article",
    jsonLd: article
      ? [
          {
            "@context": "https://schema.org",
            "@type": "Article",
            headline: article.title,
            description: article.meta_description,
            articleSection: article.category,
            keywords: article.keywords,
            image: article.image_url,
            inLanguage: "en",
            url: `${SITE_URL}/dossier/${slug}`,
            mainEntityOfPage: { "@type": "WebPage", "@id": `${SITE_URL}/dossier/${slug}` },
            author: { "@type": "Organization", name: "Licence to Play", url: SITE_URL },
            publisher: { "@type": "Organization", name: "Licence to Play", url: SITE_URL },
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
              { "@type": "ListItem", position: 2, name: "Dossier", item: `${SITE_URL}/dossier` },
              { "@type": "ListItem", position: 3, name: article.title, item: `${SITE_URL}/dossier/${slug}` },
            ],
          },
        ]
      : null,
  });

  if (status === "loading")
    return <div className="max-w-3xl mx-auto px-5 py-24 font-mono text-sm text-white/40">Decrypting briefing…</div>;

  if (status === "error")
    return (
      <div className="max-w-3xl mx-auto px-5 py-24 text-center" data-testid="article-not-found">
        <p className="overline text-crimson-bright mb-3">Error 404</p>
        <h1 className="font-display text-3xl text-white">Briefing not found</h1>
        <Link to="/dossier" className="mt-6 inline-flex items-center gap-2 text-gold hover:underline">
          <ArrowLeft className="w-4 h-4" /> Back to the archive
        </Link>
      </div>
    );

  return (
    <article data-testid="article-detail">
      {/* Header */}
      <header className="relative border-b border-white/10 grain-overlay overflow-hidden">
        <div className="absolute inset-0">
          <img src={article.image_url} alt="" className="w-full h-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-b from-background/70 to-background" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-5 sm:px-8 pt-10 pb-14">
          <nav className="flex items-center gap-1.5 overline text-white/40 mb-8">
            <Link to="/" className="hover:text-gold transition-colors">Home</Link>
            <ChevronRight className="w-3 h-3" />
            <Link to="/dossier" className="hover:text-gold transition-colors">Dossier</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-gold">{article.category}</span>
          </nav>
          <span className="overline text-gold border border-gold/30 px-2 py-1">{article.category}</span>
          <h1 className="mt-5 font-display text-4xl sm:text-5xl text-white tracking-tighter leading-[1.05]">
            {article.title}
          </h1>
          <div className="mt-5 flex items-center gap-4 overline text-white/40">
            <span className="inline-flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5" /> {article.read_time_min} min read
            </span>
            <span>Licence to Play Field Notes</span>
          </div>
        </div>
      </header>

      {/* Body + Sidebar */}
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-14 grid lg:grid-cols-[1fr_300px] gap-12">
        <div className="min-w-0">
          <div className="mb-8">
            <AdSlot size="728x90" label="In-Content" />
          </div>

          <div
            className="prose-briefing"
            data-testid="article-body"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(article.body_html) }}
          />

          <div className="mt-10 border border-crimson-bright/30 bg-crimson/10 p-6 flex gap-4">
            <ShieldAlert className="w-6 h-6 text-crimson-bright shrink-0" />
            <div>
              <p className="overline text-crimson-bright mb-1">Play Responsibly · 18+</p>
              <p className="text-sm text-white/70 leading-relaxed">
                Baccarat is a game of chance with a fixed house edge. Set limits before you play and never
                chase losses. Support:{" "}
                <a href="https://www.begambleaware.org" target="_blank" rel="noopener noreferrer" className="text-gold hover:underline">
                  BeGambleAware
                </a>
                {" · "}
                <Link to="/responsible-gambling" className="text-gold hover:underline">
                  Our RG hub
                </Link>
                .
              </p>
            </div>
          </div>
        </div>

        <aside className="space-y-6">
          <AdSlot size="300x600" label="Sidebar" sticky />
        </aside>
      </div>

      {/* Related */}
      {article.related?.length > 0 && (
        <section className="max-w-6xl mx-auto px-5 sm:px-8 pb-20">
          <p className="overline text-gold mb-6">▸ Related Briefings</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {article.related.map((a, i) => (
              <ArticleCard key={a.slug} article={a} index={i} />
            ))}
          </div>
        </section>
      )}
    </article>
  );
}
